import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import {
  Wallet,
  User,
  Mail,
  Phone,
  Calendar,
  Heart,
  Gift,
  Settings,
  Edit3,
  Award,
  Clock,
  CheckSquare,
  X,
  ChevronDown,
} from "lucide-react";

import { WalletCard } from "../components/WalletCard"; // Import the WalletCard component

export default function EditablePremiumProfile() {
  const [user, setUser] = useState(null);
  const [editedUser, setEditedUser] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editStatus, setEditStatus] = useState(null);
  const [showGenderDropdown, setShowGenderDropdown] = useState(false);
  const [showMaritalDropdown, setShowMaritalDropdown] = useState(false);
  const genderRef = useRef(null);
  const maritalRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing.");

      const decodedUser = jwtDecode(token);

      const { data } = await axios.get(
        `https://apigrand.dodunsoftsolutions.com/api/user/userbyid/${decodedUser.userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUser(data);
      setEditedUser(data);
      setStatus("ready");
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message || err.message || "Unable to load profile."
      );
      setStatus("error");
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (genderRef.current && !genderRef.current.contains(event.target)) {
        setShowGenderDropdown(false);
      }
      if (maritalRef.current && !maritalRef.current.contains(event.target)) {
        setShowMaritalDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    // Convert the date input value to ISO string format
    const date = new Date(value);
    setEditedUser((prev) => ({
      ...prev,
      [name]: date.toISOString(),
    }));
  };

  const handleSelectChange = (field, value) => {
    setEditedUser((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "gender") {
      setShowGenderDropdown(false);
    } else if (field === "maritalStatus") {
      setShowMaritalDropdown(false);
    }
  };

  const handleSave = async () => {
    try {
      setEditStatus("saving");
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication token missing.");

      const decodedUser = jwtDecode(token);

      await axios.put(
        `https://apigrand.dodunsoftsolutions.com/api/user/edituser/${decodedUser.userId}`,
        editedUser,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUser(editedUser);
      setEditStatus("success");

      localStorage.setItem("user", JSON.stringify(editedUser));

      // Reset edit status after 3 seconds
      setTimeout(() => {
        setEditStatus(null);
        setIsEditing(false);
      }, 3000);
    } catch (err) {
      console.error(err);
      setEditStatus("error");

      // Reset edit status after 3 seconds
      setTimeout(() => {
        setEditStatus(null);
      }, 3000);
    }
  };

  const handleCancel = () => {
    setEditedUser(user);
    setIsEditing(false);
  };

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-red-950 to-slate-900">
        <div className="flex flex-col items-center">
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-4 border-red-800 border-opacity-40"></div>
            <div className="absolute inset-0 rounded-full border-4 border-red-500 border-t-transparent animate-spin"></div>
          </div>
          <p className="mt-6 text-red-300 font-medium">
            Loading your profile...
          </p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="max-w-md mx-auto p-8 mt-20 bg-gradient-to-b from-red-900 to-slate-900 border border-red-800/50 rounded-lg shadow-md">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-red-950 rounded-full">
            <Settings className="w-8 h-8 text-red-400" />
          </div>
        </div>
        <h3 className="text-xl font-semibold text-center text-red-300 mb-2">
          Connection Error
        </h3>
        <p className="text-red-200/80 text-center mb-6">{error}</p>
        <button
          className="w-full py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold rounded-md shadow-md flex items-center justify-center space-x-2 transition-all duration-300"
          onClick={() => window.location.reload()}
        >
          <Clock className="w-4 h-4" />
          <span>Try Again</span>
        </button>
      </div>
    );
  }

  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Format date for input field
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  // Get first name
  const firstName = user.name.includes("@")
    ? user.name.split("@")[0]
    : user.name.split(" ")[0];

  return (
    <div className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Edit Status Notification */}
        {editStatus && (
          <div
            className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3 border
            ${
              editStatus === "saving"
                ? "bg-red-900/90 text-red-200 border-red-600/50"
                : editStatus === "success"
                ? "bg-emerald-900/90 text-emerald-200 border-emerald-600/50"
                : "bg-red-800/90 text-red-200 border-red-500/50"
            }`}
          >
            {editStatus === "saving" && (
              <>
                <div className="animate-spin w-5 h-5 border-2 border-red-400 border-t-transparent rounded-full"></div>
                <span>Saving changes...</span>
              </>
            )}

            {editStatus === "success" && (
              <>
                <CheckSquare className="w-5 h-5" />
                <span>Profile updated successfully!</span>
              </>
            )}

            {editStatus === "error" && (
              <>
                <X className="w-5 h-5" />
                <span>Failed to update profile. Please try again.</span>
              </>
            )}
          </div>
        )}

        {/* Header Card */}
        <div className="bg-gradient-to-r from-red-200 to-slate-100 shadow-xl rounded-2xl overflow-hidden mb-6 border border-slate-300">
          <div className="p-8 text-slate-900 relative">
            {/* Wallet Points Badge - We'll display this from the wallet component */}
            <div className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6">
              {/* Avatar */}
              <div className="w-24 h-24 bg-gradient-to-br from-red-200 to-slate-100 rounded-full p-1 shadow-lg border-2 border-red-300">
                <div className="w-full h-full bg-slate-100 rounded-full flex items-center justify-center">
                  <User className="w-12 h-12 text-red-800" />
                </div>
              </div>

              {/* User info */}
              <div className="text-center md:text-left">
                <h1 className="text-3xl font-bold text-red-800">
                  Welcome, {firstName}
                </h1>
                <p className="text-red-700">@{user.email.split("@")[0]}</p>

                <div className="mt-4 flex items-center justify-center md:justify-start space-x-3">
                  <div className="bg-slate-100 rounded-full px-3 py-1 flex items-center border border-slate-300">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        user.isVerified ? "bg-emerald-400" : "bg-red-700"
                      }`}
                    ></span>
                    <span className="ml-2 text-xs font-medium">
                      {user.isVerified ? "Verified" : "Unverified"}
                    </span>
                  </div>

                  <div className="bg-slate-100 rounded-full px-3 py-1 flex items-center border border-slate-300">
                    <Clock className="w-3 h-3 text-red-700" />
                    <span className="ml-2 text-xs font-medium">
                      Since{" "}
                      {new Date(user.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center flex-wrap divide-x divide-slate-300 border-t border-slate-300">
            <QuickAction
              icon={<Edit3 className="w-4 h-4 mr-2 text-white" />}
              label={isEditing ? "Editing..." : "Edit Profile"}
              onClick={() => !isEditing && setIsEditing(true)}
              active={isEditing}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Personal Information */}
          <div className="lg:col-span-2 bg-white shadow-xl rounded-xl overflow-hidden border border-slate-300">
            <div className="border-b border-slate-200 px-6 py-4 bg-gradient-to-r from-red-200 to-slate-100 text-white">
              <h2 className="text-xl font-semibold text-red-700 flex items-center">
                <User className="w-5 h-5 mr-2 text-red-800" />
                Personal Information
              </h2>
              {isEditing && (
                <div className="flex items-center space-x-2 text-sm mt-1">
                  <span className="text-red-700">Edit Mode</span>
                  <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse"></div>
                </div>
              )}
            </div>

            <div className="p-6 space-y-6 text-slate-800">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name Field */}
                {isEditing ? (
                  <EditableField
                    icon={<User className="w-5 h-5 text-red-500" />}
                    label="Full Name"
                    name="name"
                    value={editedUser.name}
                    onChange={handleChange}
                  />
                ) : (
                  <ProfileItem
                    icon={<User className="w-5 h-5 text-red-500" />}
                    label="Full Name"
                    value={user.name}
                  />
                )}

                {/* Email Field (readonly) */}
                <ProfileItem
                  icon={<Mail className="w-5 h-5 text-red-500" />}
                  label="Email"
                  value={user.email}
                />

                {/* Phone Field */}
                {isEditing ? (
                  <EditableField
                    icon={<Phone className="w-5 h-5 text-red-500" />}
                    label="Phone"
                    name="phone"
                    value={editedUser.phone || ""}
                    onChange={handleChange}
                  />
                ) : (
                  <ProfileItem
                    icon={<Phone className="w-5 h-5 text-red-500" />}
                    label="Phone"
                    value={user.phone || "Not provided"}
                  />
                )}

                {/* Gender Field */}
                {isEditing ? (
                  <div
                    className="flex items-start space-x-3 relative"
                    ref={genderRef}
                  >
                    <div className="p-2 bg-red-100 rounded-md border border-red-200">
                      <User className="w-5 h-5 text-red-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-red-700 mb-1.5">Gender</p>
                      <div
                        onClick={() =>
                          setShowGenderDropdown(!showGenderDropdown)
                        }
                        className="flex justify-between items-center w-full py-2 px-3 bg-white border border-slate-300 rounded-md cursor-pointer text-slate-800 hover:border-red-400 transition-colors"
                      >
                        <span className="font-medium">
                          {editedUser.gender || "Select Gender"}
                        </span>
                        <ChevronDown className="w-4 h-4 text-red-500" />
                      </div>

                      {showGenderDropdown && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-xl">
                          {["Male", "Female", "Other", "Prefer not to say"].map(
                            (option) => (
                              <div
                                key={option}
                                className="px-4 py-2 hover:bg-red-50 cursor-pointer text-slate-800 hover:text-red-700 transition-colors"
                                onClick={() =>
                                  handleSelectChange("gender", option)
                                }
                              >
                                {option}
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  user.gender && (
                    <ProfileItem
                      icon={<User className="w-5 h-5 text-red-500" />}
                      label="Gender"
                      value={user.gender}
                    />
                  )
                )}

                {/* Marital Status Field */}
                {isEditing ? (
                  <div
                    className="flex items-start space-x-3 relative"
                    ref={maritalRef}
                  >
                    <div className="p-2 bg-red-100 rounded-md border border-red-200">
                      <Heart className="w-5 h-5 text-red-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-red-700 mb-1.5">
                        Marital Status
                      </p>
                      <div
                        onClick={() =>
                          setShowMaritalDropdown(!showMaritalDropdown)
                        }
                        className="flex justify-between items-center w-full py-2 px-3 bg-white border border-slate-300 rounded-md cursor-pointer text-slate-800 hover:border-red-400 transition-colors"
                      >
                        <span className="font-medium">
                          {editedUser.maritalStatus || "Select Status"}
                        </span>
                        <ChevronDown className="w-4 h-4 text-red-500" />
                      </div>

                      {showMaritalDropdown && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-xl">
                          {[
                            "Single",
                            "Married",
                            "Divorced",
                            "Widowed",
                            "Prefer not to say",
                          ].map((option) => (
                            <div
                              key={option}
                              className="px-4 py-2 hover:bg-red-50 cursor-pointer text-slate-800 hover:text-red-700 transition-colors"
                              onClick={() =>
                                handleSelectChange("maritalStatus", option)
                              }
                            >
                              {option}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  user.maritalStatus && (
                    <ProfileItem
                      icon={<Heart className="w-5 h-5 text-red-500" />}
                      label="Marital Status"
                      value={user.maritalStatus}
                    />
                  )
                )}

                {/* Date of Birth Field */}
                {isEditing ? (
                  <DateField
                    icon={<Calendar className="w-5 h-5 text-red-500" />}
                    label="Date of Birth"
                    name="dob"
                    value={formatDateForInput(editedUser.dob)}
                    onChange={handleDateChange}
                  />
                ) : (
                  user.dob && (
                    <ProfileItem
                      icon={<Calendar className="w-5 h-5 text-red-500" />}
                      label="Date of Birth"
                      value={formatDate(user.dob)}
                    />
                  )
                )}

                {/* Anniversary Field */}
                {isEditing ? (
                  <DateField
                    icon={<Gift className="w-5 h-5 text-red-500" />}
                    label="Anniversary"
                    name="anniversary"
                    value={formatDateForInput(editedUser.anniversary)}
                    onChange={handleDateChange}
                  />
                ) : (
                  user.anniversary && (
                    <ProfileItem
                      icon={<Gift className="w-5 h-5 text-red-500" />}
                      label="Anniversary"
                      value={formatDate(user.anniversary)}
                    />
                  )
                )}
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t border-slate-200">
                {isEditing ? (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleSave}
                      className="flex-1 py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-bold rounded-lg shadow-lg transition-all duration-300 flex items-center justify-center space-x-2"
                    >
                      <CheckSquare className="w-4 h-4" />
                      <span>Save Changes</span>
                    </button>

                    <button
                      onClick={handleCancel}
                      className="flex-1 py-2.5 bg-slate-200 text-slate-700 font-medium rounded-lg border border-slate-300 hover:bg-slate-300 transition-colors flex items-center justify-center space-x-2"
                    >
                      <X className="w-4 h-4" />
                      <span>Cancel</span>
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="w-full py-2.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-bold rounded-lg transition-all duration-300 shadow-lg flex items-center justify-center space-x-2"
                  >
                    <Edit3 className="w-4 h-4" />
                    <span>Edit Information</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileItem({ icon, label, value }) {
  return (
    <div className="flex items-start space-x-3">
      <div className="p-2 bg-red-100 rounded-md border border-red-200">
        {icon}
      </div>
      <div>
        <p className="text-sm text-red-700 mb-1">{label}</p>
        <p className="font-medium text-slate-800">{value || "Not provided"}</p>
      </div>
    </div>
  );
}

function EditableField({ icon, label, name, value, onChange }) {
  return (
    <div className="flex items-start space-x-3">
      <div className="p-2 bg-red-100 rounded-md border border-red-200">
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-sm text-red-700 mb-1.5">{label}</p>
        <input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          className="w-full py-2 px-3 bg-white border border-slate-300 rounded-md focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none transition-all text-slate-800 placeholder-slate-400"
        />
      </div>
    </div>
  );
}

function DateField({ icon, label, name, value, onChange }) {
  return (
    <div className="flex items-start space-x-3">
      <div className="p-2 bg-red-100 rounded-md border border-red-200">
        {icon}
      </div>
      <div className="flex-1">
        <p className="text-sm text-red-700 mb-1.5">{label}</p>
        <input
          type="date"
          name={name}
          value={value}
          onChange={onChange}
          className="w-full py-2 px-3 bg-white border border-slate-300 rounded-md focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none transition-all text-slate-800"
        />
      </div>
    </div>
  );
}

function QuickAction({ icon, label, onClick, active }) {
  // Force any icon to render white
  const whiteIcon = React.cloneElement(icon, {
    className: `${icon.props.className ?? ""} text-white`,
  });

  return (
    <button
      onClick={onClick}
      className={`
        flex-1 py-3 flex items-center justify-center space-x-1
        transition-transform transition-colors min-w-24
        ${
          active
            ? "bg-red-900 text-red-300"
            : "bg-red-800 text-red-100 hover:bg-red-700 hover:text-red-50"
        }
        hover:scale-105
      `}
    >
      {whiteIcon}
      <span className="text-sm">{label}</span>
    </button>
  );
}
